import { ChangeDetectionStrategy, Component, ElementRef, HostListener, Input } from '@angular/core';
import type { HsCameraView } from '@lib/core-ts/tools-new/camera/camera-views.function';
import type { HypersceneAdvancedDemo } from '../../hyperscene-advanced.component';

/** A standard view in the views menu: its view, label and keyboard shortcut. */
interface ViewerToolbarView {
  view: HsCameraView;
  label: string;
  key: string;
}

/**
 * The toolbar over the 3D view with the everyday tools of CAD and BIM viewers as icon buttons: home and zoom,
 * standard views, measure, section, x-ray and visibility. Each button names itself in a label shown on hover or
 * keyboard focus. The page owns the state; the Tools tab holds the settings.
 */
@Component({
  selector: 'nav[viewer-toolbar]',
  templateUrl: './viewer-toolbar.component.html',
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: false,
})
export class ViewerToolbarComponent {
  @Input({ required: true }) public viewer!: HypersceneAdvancedDemo;

  /** Whether the standard views menu is open. */
  public viewsOpen: boolean = false;

  public readonly views: readonly ViewerToolbarView[] = [
    { view: 'front', label: 'Front', key: '1' },
    { view: 'back', label: 'Back', key: '2' },
    { view: 'left', label: 'Left', key: '3' },
    { view: 'right', label: 'Right', key: '4' },
    { view: 'top', label: 'Top', key: '5' },
    { view: 'bottom', label: 'Bottom', key: '6' },
    { view: 'iso', label: 'Isometric', key: '0' },
  ];

  public constructor(private readonly host: ElementRef<HTMLElement>) {}

  /** A click anywhere outside the toolbar, or Escape, closes the views menu. */
  @HostListener('document:click', ['$event'])
  public onDocumentClick(event: MouseEvent): void {
    if (this.viewsOpen && !(event.target instanceof Node && this.host.nativeElement.contains(event.target))) {
      this.viewsOpen = false;
    }
  }

  @HostListener('document:keydown.escape')
  public onEscape(): void {
    this.viewsOpen = false;
  }

  public showView(view: HsCameraView): void {
    this.viewsOpen = false;
    void this.viewer.setCameraView(view);
  }
}
